import { useCallback, useContext, useEffect, useState } from 'react';
import {
  /* useShowPopup, */ useInitData,
} from '@vkruglikov/react-telegram-web-app';
import { getOkxLanguage } from '../v2/utils/utils';
import { useProxyLocalStorage } from '../v2/ProxyLocalStorage';
import { Locales } from '@okxconnect/ui';
import type { OKXUniversalConnectUI } from '@okxconnect/ui';
import { THEME } from '../config/okxConnect';
import { TomoContext } from '..';
import { supportedEvmChainIdDefault } from '../v2/constant';

let okxUniversalUI: any;

function proxifyOkxProvider(okxProvider: OKXUniversalConnectUI) {
  const handler: ProxyHandler<OKXUniversalConnectUI> = {
    get(target: OKXUniversalConnectUI, property, receiver) {
      switch (property) {
        case 'isConnected': {
          return (target as any).connected();
        }
        default:
          return Reflect.get(target, property, receiver);
      }
    }
  };
  return new Proxy(okxProvider, handler)
}

interface Option {
  connectSuccess?: () => void;
  connectError?: () => void;
  metaData?: OkxConnectMeta;
  chain?: string;
  theme?: 'dark' | 'light';
}

interface OkxConnectMeta {
  name: string;
  icon: string;
}

const useOkxEVMConnect = (option?: Option) => {
  const {
    // connectSuccess,
    // connectError,
    metaData = { name: 'dapp', icon: ' ' },
    theme,
  } = option || {};
  const [okxUniversalProvider, setOkxUniversalProvider] = useState(null);
  const [connected, setConnected] = useState(false); // this turns out to be crucial for update
  // const [account, setAccount] = useState(''); // this does NOT seems to be important right now
  const [initDataUnsafe] = useInitData();

  const {
    setProviders,
    supportedProviders: [chain],
    useEvmChains
  } = useContext(TomoContext);

  const storage = useProxyLocalStorage();
  const connectType = storage.get('connect_type');

  /** init okxUniversalProvider */
  const getOkxProvider = async () => {
    if (chain !== 'EVM') return null;
    const language = getOkxLanguage(initDataUnsafe?.user?.language_code);
    if (!okxUniversalUI) {
      const OKXUniversalConnectUI = (await import('@okxconnect/ui'))
        .OKXUniversalConnectUI;
      const okxUniversalUIOriginal = await OKXUniversalConnectUI.init({
        dappMetaData: metaData,
        actionsConfiguration: {
          returnStrategy: 'tg://resolve',
          modals: 'all',
          tmaReturnUrl: 'back',
        },
        language: language as Locales,
        uiPreferences: {
          theme: theme === 'dark' ? THEME.DARK : THEME.LIGHT,
        },
      });
      okxUniversalUI = proxifyOkxProvider(okxUniversalUIOriginal)
      // okxUniversalUI = (okxUniversalUIOriginal)
    }
    setOkxUniversalProvider(okxUniversalUI);
    if (okxUniversalUI?.connected?.()) setConnected(true);
    return okxUniversalUI;
  };

  const handleConnectSuccess = (session, provider) => {
    const accountWithChainId = session?.namespaces?.eip155?.accounts?.[0] || '';
    if (accountWithChainId) {
      const chainId = session?.namespaces?.eip155?.chains?.[0];
      const account = accountWithChainId.split(chainId + ':')?.[1];
      window.ethereum = provider; 
      setConnected(true);
      return account;
    }
    return '';
  };

  // auto reconnect
  useEffect(() => {
    if (chain === 'EVM' && connectType === 'OKX') {
      getOkxProvider().then(provider => {
        window.ethereum = provider;
      });
    }
  }, [connectType, chain]);

  const disconnectEffect = useCallback(
    (input: any) => {
      console.log('disconnecting', input);
      setConnected(false);
      storage.set('connect_type', '');
      window.ethereum = null;
    },
    [storage]
  );

  // listen to disconnect event
  useEffect(() => {
    if (okxUniversalProvider) {
      okxUniversalProvider.on('session_delete', disconnectEffect);
      return () => {
        okxUniversalProvider.removeListener('session_delete', disconnectEffect);
      };
    }
  }, [disconnectEffect, okxUniversalProvider]);

  /** connect wallet */
  const connect = async () => {
    let provider = okxUniversalProvider;
    if (!provider) {
      provider = await getOkxProvider();
    }
    if (provider.isConnected) {
      console.log({ message: 'Already connected' });
      storage.set('connect_type', 'OKX');
      setConnected(true)
      return await okxUniversalProvider.request({
        method: 'eth_requestAccounts'
      }); // TODO
    }
    const session = await provider.openModal({
      namespaces: {
        eip155: {
          chains: useEvmChains.map(id => `eip155:${id}`),
          // chains: ['eip155:1', 'eip155:56'],
          rpcMap: {
            1: 'https://rpc.ankr.com/eth', // set your own rpc url
          },
          defaultChain: '1',
        },
      },
    });
    if (session) {
      storage.set('connect_type', 'OKX');
      return handleConnectSuccess(session, provider);
    }
    return '';
  };

  const disconnect = useCallback(() => {
    okxUniversalProvider?.disconnect?.();
  }, [okxUniversalProvider]);

  // watch okx provider status
  useEffect(() => {
    if (okxUniversalProvider?.isConnected)
      setProviders(pre => ({
        ...pre,
        ethereum: okxUniversalProvider,
      }
      ));
    else {
      setProviders(pre => ({
        ...pre,
        ethereum: null,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [okxUniversalProvider, okxUniversalProvider?.isConnected])

  return {
    okxUniversalProvider,
    connect,
    request: okxUniversalProvider?.request, // no use so far
    disconnect, // no use so far
    connected,
  };
};

export default useOkxEVMConnect;
